import React, { useState, useEffect } from 'react'
import { XIcon } from 'lucide-react'
import { ApiService } from '../../../api/services.gen'
import { useAlert } from '../../../constants/AlertProvider'

export default function EditSkills({ applicationId }) {
    const [skills, setSkills] = useState([])
    const [allSkills, setAllSkills] = useState([])
    const [selectedSkill, setSelectedSkill] = useState('')
    const [newSkillScore, setNewSkillScore] = useState(5)
    const [search, setSearch] = useState('')
    const showAlert = useAlert();

    // Fetch user's skills
    useEffect(() => {
        const fetchUserSkills = async () => {
            try {
                const res = await ApiService.apiSkillList({ application: applicationId })
                setSkills(res.results || [])
            } catch (e) {
                showAlert("Error fetching skills", "error");
            }
        }
        fetchUserSkills()
    }, [applicationId])

    // Fetch all available skills
    useEffect(() => {
        const fetchAllSkills = async () => {
            try {
                const res = await ApiService.apiMatchingSkillsList({ limit: 1000 })
                setAllSkills(res.results || res)
            } catch (e) {
                setAllSkills([])
                showAlert("Error fetching skills", "error");
            }
        }
        fetchAllSkills()
    }, [])

    // Add skill
    const addSkill = async (e) => {
        e.preventDefault()
        if (!selectedSkill || skills.some(s => s.id === Number(selectedSkill))) return
        try {
            const res = await ApiService.apiSkillCreate({
                requestBody: {
                    application: applicationId,
                    name: selectedSkill.name,
                    score: newSkillScore,
                }
            })
            setSkills([...skills, res])
            setSelectedSkill('')
            setNewSkillScore(5)
            setSearch('')
            showAlert("Skill added successfully", "success");
        } catch (e) {
            showAlert("Error adding skills", "error");
            console.error(e)
        }
    }

    // Remove skill
    const removeSkill = async (skillId) => {
        try {
            await ApiService.apiSkillDestroy({ id: skillId })
            setSkills(skills.filter((s) => s.id !== skillId))
        } catch (e) {
            console.error(e)
        }
    }

    // const updateSkill = async (skillId, field, value) => {
    //     try {
    //         // Update on backend
    //         await ApiService.apiSkillPartialUpdate({
    //             id: skillId,
    //             requestBody: { [field]: value }
    //         });
    //         // Update in local state
    //         setSkills(skills =>
    //             skills.map(skill =>
    //                 skill.id === skillId ? { ...skill, [field]: value } : skill
    //             )
    //         );
    //     } catch (e) {
    //         console.error(e);
    //     }
    // };

    const filteredSkills = allSkills
        .filter(skill => !skills.some(s => s.name === skill.name))
        .filter(skill => skill.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-4">
                {skills.map((skill) => (
                    <div
                        key={skill.id}
                        className="flex flex-col items-start bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 px-3 py-1 rounded-full text-sm mb-2"
                    >
                        <div className="flex items-center">
                            {skill.name}
                            <button
                                type="button"
                                onClick={() => removeSkill(skill.id)}
                                className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 focus:outline-none"
                            >
                                <XIcon size={16} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <form onSubmit={addSkill} className="flex flex-col gap-y-2">
                <input
                    type="text"
                    placeholder="Search skills..."
                    value={search}
                    onChange={e => {
                        setSearch(e.target.value)
                        setSelectedSkill('')
                    }}
                    className="block px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm mt-1 mb-2">
                    {filteredSkills.length === 0 && (
                        <div className="p-2 text-gray-400 dark:text-gray-500 text-sm">No skills found</div>
                    )}
                    {filteredSkills.map(skill => (
                        <div
                            key={skill.id}
                            className={`px-3 py-2 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-900 dark:text-gray-100 ${selectedSkill && selectedSkill.id === skill.id ? 'bg-blue-200 dark:bg-blue-900/50' : ''}`}
                            onClick={() => setSelectedSkill(skill)}
                        >
                            {skill.name}
                        </div>
                    ))}
                </div>
                {selectedSkill && (
                    <div className="flex flex-col items-center mt-2 mb-2">
                        <label className="text-md font-medium text-gray-700 dark:text-gray-300">
                            Proficiency: <span className="text-blue-600 font-semibold">{newSkillScore}</span>/10
                        </label>
                        <input
                            type="range"
                            min={0}
                            max={10}
                            value={newSkillScore}
                            onChange={e => setNewSkillScore(Number(e.target.value))}
                            className="w-full h-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg appearance-none cursor-pointer accent-blue-500 hover:bg-blue-200 transition-all duration-200"
                        />
                    </div>
                )}
                <div className='flex justify-end'>
                <button
                    type="submit"
                    disabled={!selectedSkill}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full disabled:bg-blue-300"
                >
                    Add
                </button>
                </div>
            </form>
            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Add skills that showcase your abilities and make you stand out to
                employers.
            </div>
        </div>
    )
}